import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  Activity,
  FileText,
  ShoppingBag,
  ChefHat,
  LogOut,
  Shield,
  TrendingUp,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export const SuperAdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/restaurants", icon: Building2, label: "Restaurants" },
    { path: "/admin/employees", icon: Users, label: "Employees" },
    { path: "/admin/orders", icon: ShoppingBag, label: "Orders" },
    { path: "/admin/kitchen-monitor", icon: ChefHat, label: "Kitchen Monitor" },
    {
      section: "Reports & Analytics",
      items: [
        { path: "/admin/reports/sales", icon: TrendingUp, label: "Sales Reports" },
        {
          path: "/admin/reports/commission",
          icon: DollarSign,
          label: "Commission Reports",
        },
        {
          path: "/admin/reports/performance",
          icon: FileText,
          label: "Performance Reports",
        },
      ],
    },
    {
      section: "System & Security",
      items: [
        { path: "/admin/login-history", icon: Activity, label: "Login History" },
        { path: "/admin/activity-logs", icon: Shield, label: "Audit Logs" },
      ],
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`group flex items-center gap-3 px-4 py-4 rounded-lg transition-all duration-200 ${
          isActive
            ? "bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-md"
            : "text-gray-600 hover:bg-red-50 hover:text-red-600"
        }`}
      >
        <Icon size={18} className="shrink-0" />
        <span className="font-medium text-sm">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r border-gray-200 shadow-xl flex flex-col">
        {/* Logo */}
        <div className="px-6 py-8 bg-gradient-to-r from-red-600 to-orange-500">
          <h1 className="text-2xl font-bold text-white tracking-wide">
            ORDERPOZ
          </h1>
          <p className="text-xs text-red-100 mt-1 flex items-center gap-2">
            <Shield size={14} /> Super Admin
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-2 overflow-y-auto">
          {navItems.map((item, index) => {
            if (item.path) return renderNavItem(item);

            if (item.section) {
              return (
                <div key={index}>
                  <p className="px-4 py-4 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {item.section}
                  </p>
                  <div className="space-y-1">
                    {item.items.map((sub) => renderNavItem(sub))}
                  </div>
                </div>
              );
            }

            return null;
          })}
        </nav>

        {/* Footer / User */}
        <div className="p-4 border-t bg-gray-50">
          <div className="mb-3 px-2">
            <p className="text-sm font-semibold text-gray-900">
              Super Administrator
            </p>
            <p className="text-xs text-gray-500">System Control Panel</p>
          </div>

          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full flex items-center gap-2 justify-center border-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
          >
            <LogOut size={16} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
};
